import React, { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Heart, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { trackDonationStart } from "../lib/analytics";

const PRESETS = [
    { key: "d5", amount: 5, label: "$5", note: "1 meal" },
    { key: "d10", amount: 10, label: "$10", note: "Small kit" },
    { key: "d25", amount: 25, label: "$25", note: "Family" },
    { key: "d50", amount: 50, label: "$50", note: "Community" },
    { key: "d100", amount: 100, label: "$100", note: "Big impact" },
];

export default function Donate() {
    const [selected, setSelected] = useState("d25");
    const [custom, setCustom] = useState("");
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [impact, setImpact] = useState({ raised_usd: 0, donations_count: 0, goal_usd: 100000 });
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/impact/counter").then((r) => setImpact(r.data)).catch(() => {});
        if (searchParams.get("session_id") && window.location.pathname === "/donate/success") {
            // handled by success page
        }
    }, [searchParams]);

    const donate = async () => {
        setLoading(true);
        try {
            const body = { origin_url: window.location.origin, donor_name: name, donor_message: message };
            if (selected === "custom") {
                const amt = parseFloat(custom);
                if (!amt || amt < 1) { toast.error("Enter at least $1"); setLoading(false); return; }
                body.custom_amount = amt;
            } else {
                body.preset = selected;
            }
            const { data } = await api.post("/donations/checkout", body);
            trackDonationStart(body.custom_amount || (selected === "d5" ? 5 : selected === "d10" ? 10 : selected === "d25" ? 25 : selected === "d50" ? 50 : 100));
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout");
            setLoading(false);
        }
    };

    const pct = Math.min(100, (impact.raised_usd / (impact.goal_usd || 1)) * 100);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
            <div className="grid lg:grid-cols-5 gap-10">
                <div className="lg:col-span-3">
                    <p className="text-xs uppercase tracking-widest text-primary mb-3">Direct donation</p>
                    <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight leading-tight">
                        100% goes to <span className="text-primary">Hands of Hope</span>.
                    </h1>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" data-testid="donate-verified-charity-badge">
                        ✓ Verified charity partner
                    </div>
                    <p className="text-muted-foreground text-lg mt-4 max-w-xl leading-relaxed">
                        Every direct dollar donated on Real-Check is passed through to the Hands of Hope charity foundation.
                        No fees, no cuts, no exceptions.
                    </p>

                    <Card className="card-surface p-8 rounded-2xl mt-10">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount</Label>
                        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => { setSelected(p.key); setCustom(""); }}
                                    data-testid={`donate-preset-${p.key}`}
                                    className={`rounded-xl border py-3 px-2 text-center transition-colors ${
                                        selected === p.key
                                            ? "border-primary bg-primary/20 text-accent"
                                            : "border-white/10 hover:border-white/30 text-muted-foreground"
                                    }`}
                                >
                                    <div className="font-display font-bold text-lg">{p.label}</div>
                                    <div className="text-[10px] uppercase tracking-widest opacity-70">{p.note}</div>
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => setSelected("custom")}
                                data-testid="donate-preset-custom"
                                className={`rounded-xl border py-3 px-2 text-center transition-colors ${
                                    selected === "custom" ? "border-primary bg-primary/20 text-accent" : "border-white/10 hover:border-white/30 text-muted-foreground"
                                }`}
                            >
                                <div className="font-display font-bold text-lg">Custom</div>
                                <div className="text-[10px] uppercase tracking-widest opacity-70">Any</div>
                            </button>
                        </div>
                        {selected === "custom" && (
                            <div className="mt-4">
                                <Label htmlFor="custom" className="text-xs uppercase tracking-widest text-muted-foreground">Custom amount (USD)</Label>
                                <Input
                                    id="custom"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={custom}
                                    onChange={(e) => setCustom(e.target.value)}
                                    placeholder="25"
                                    className="mt-1.5 bg-white/5 border-white/10 h-11"
                                    data-testid="donate-custom-input"
                                />
                            </div>
                        )}

                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="dn" className="text-xs uppercase tracking-widest text-muted-foreground">Your name (optional)</Label>
                                <Input id="dn" value={name} onChange={(e) => setName(e.target.value)} placeholder="Anonymous" className="mt-1.5 bg-white/5 border-white/10 h-11" data-testid="donate-name-input" />
                            </div>
                            <div>
                                <Label htmlFor="dm" className="text-xs uppercase tracking-widest text-muted-foreground">Message (optional)</Label>
                                <Input id="dm" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Keep going!" className="mt-1.5 bg-white/5 border-white/10 h-11" data-testid="donate-message-input" />
                            </div>
                        </div>

                        <Button
                            onClick={donate}
                            disabled={loading}
                            data-testid="donate-checkout-button"
                            className="w-full h-12 mt-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base shadow-lg shadow-primary/30"
                        >
                            {loading ? "Redirecting…" : (<><Heart className="h-4 w-4 mr-2" /> Donate to Hands of Hope</>)}
                        </Button>
                        <p className="text-[11px] text-muted-foreground mt-3 text-center">
                            Secure payment via Stripe. 100% goes to Hands of Hope.
                        </p>
                    </Card>
                </div>

                <div className="lg:col-span-2 space-y-6">
                    <Card className="card-surface p-6 rounded-2xl">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Live impact</p>
                        <h3 className="font-display text-3xl text-accent" data-testid="donate-impact-raised">
                            ${impact.raised_usd.toLocaleString()}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">raised for Hands of Hope</p>
                        <div className="mt-4 w-full h-2 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-primary to-emerald-400" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">
                            {impact.donations_count} donations · goal ${impact.goal_usd.toLocaleString()}
                        </p>
                    </Card>

                    <Card className="card-surface p-6 rounded-2xl">
                        <h4 className="font-display text-accent text-lg mb-3">Where your money goes</h4>
                        <ul className="text-sm text-muted-foreground space-y-2">
                            <li className="flex items-start gap-2"><span className="text-primary">→</span> 100% of direct donations forwarded to Hands of Hope</li>
                            <li className="flex items-start gap-2"><span className="text-primary">→</span> Stripe processing fees covered by Real-Check</li>
                            <li className="flex items-start gap-2"><span className="text-primary">→</span> Quarterly transparency reports published</li>
                        </ul>
                        <Link to="/transparency" className="text-primary text-sm mt-4 inline-flex items-center gap-1 hover:underline" data-testid="donate-see-transparency-link">
                            Read the transparency report <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </Card>

                    <Card className="card-surface p-6 rounded-2xl" data-testid="donate-mission-card">
                        <p className="text-xs uppercase tracking-widest text-primary mb-2">The mission</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            Real-Check.org was built to help people and animals through the mission of{" "}
                            <span className="text-accent font-semibold">Hands of Hope</span> (founded in 2025).
                            Created by Chris, a disabled veteran — dedicated to his mama and Charlie.
                        </p>
                        <Link to="/about" className="text-primary text-sm mt-3 inline-flex items-center gap-1 hover:underline" data-testid="donate-read-story-link">
                            Read the story <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </Card>
                </div>
            </div>
        </div>
    );
}
